import type { ExternalNetworkSignalDto } from '@terra/shared-contracts-external-network';
import type { InternalNetworkSignalDto } from '@terra/shared-contracts-internal-network';
import type { GroupedSignal } from '@terra/shared-contracts-signals';
import { deriveSignalSourceFromOrigin } from '@terra/shared-contracts-signals';
import type { SignalAssetTypeType } from '@terra/shared-schemas-orchestrator';
import {
  SignalOriginSchema,
  SignalRankSchema,
} from '@terra/shared-schemas-orchestrator';

const SURFACE_SIGNAL_EPOCH_DATE = new Date(0);

const resolveSignalAssetType = (
  entityKind: string | null | undefined,
): SignalAssetTypeType | null => {
  if (
    entityKind === 'DOMAIN' ||
    entityKind === 'HOST' ||
    entityKind === 'NETWORK_SERVICE' ||
    entityKind === 'SOFTWARE'
  ) {
    return entityKind;
  }

  return null;
};

export const mapSurfaceSignalToGroupedSignal = (
  signal: ExternalNetworkSignalDto | InternalNetworkSignalDto,
): GroupedSignal => {
  const createdAt = signal.createdAt ?? SURFACE_SIGNAL_EPOCH_DATE;
  const assetType = resolveSignalAssetType(signal.targetEntityKind ?? null);

  const baseSignal = {
    id: signal.id,
    tenantId: null,
    applicationId: '',
    targetId: signal.targetId,
    category: signal.category,
    cweIds: signal.cweIds ?? [],
    owaspCategory: signal.owaspCategory ?? null,
    rank: SignalRankSchema.enum.UNRANKED,
    stage: signal.stage,
    status: signal.status,
    confirmedVulnerabilityFindingState: null,
    endpointId: null,
    harEntryId: null,
    entryPointId: null,
    securityTestCaseId: signal.securityTestCaseId ?? null,
    securityTestCaseSummary: null,
    description: signal.description ?? null,
    analysis: null,
    confidence: signal.confidence ?? null,
    reason: null,
    blockedReason: null,
    isAuthenticated: null,
    smokeTestSummary: signal.smokeTestSummary ?? null,
    lastSmokeTestedAt: signal.lastSmokeTestedAt ?? null,
    assigneeId: signal.assigneeId ?? null,
    isActive: signal.isActive,
    sensitive: false,
    prUrl: null,
    validatedByCode: null,
    validationRejected: signal.validationRejected ?? null,
    validationReasoning: null,
    isAppWide: null,
    metadata: null,
    sourceLabel: null,
    sourceProvider: null,
    importBatchId: null,
    source: deriveSignalSourceFromOrigin(signal.origin),
    assetId: signal.targetEntityId ?? null,
    assetType,
    attackScope: null,
    dedupeFingerprint: null,
    createdAt,
    updatedAt: createdAt,
    endpoint: null,
    targetSummary: signal.targetSummary ?? null,
    ...(signal.linkedFinding ? { linkedFinding: signal.linkedFinding } : {}),
  };

  if (signal.origin === SignalOriginSchema.enum.FINDING_CHAINING) {
    return {
      ...baseSignal,
      origin: signal.origin,
      constituentFindingIds: [],
    };
  }

  return {
    ...baseSignal,
    origin: signal.origin,
  };
};
